import { SHStack, SHTypography } from "@components/design-systems";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import {
  AnalysisSummaryDTO,
  PortfolioCompositionDTO,
  PortfolioCompositionSectionDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { sortBySubProductOrder } from "@pages/platform-analysis-v2/_id/utils";
import { RootState } from "@redux/store";
import { chunk } from "lodash";
import { memo, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { AnalysisSummaryStyleConfigs as Configs } from "../../../configs";
import { SummaryPortfolioCompositionTable } from "../table-components/table";
interface SummaryPortfolioCompositionContainerProps {
  title?: string | JSX.Element;
  showControls?: boolean;
  disabled?: boolean;
  getPortfolioCompositionDataFunc?: (
    state: RootState,
  ) => PortfolioCompositionDTO | undefined;
  getSmaSelectedSubProductsFunc?: (
    state: RootState,
  ) => SelectedSubProductDTO[] | undefined;
  getHiddenIndexesMapFunc?: (state: RootState) => boolean[] | undefined;
}

export const SummaryPortfolioCompositionContainer = memo(
  ({
    title = "Portfolio composition by platform",
    showControls = true,
    disabled,
    getPortfolioCompositionDataFunc = (state: RootState) =>
      state.platformAnalysis?.platformAnalysis?.summary
        ?.portfolioCompositionByPlatform,
    getSmaSelectedSubProductsFunc = (state: RootState) =>
      state.platformAnalysis?.platformAnalysis?.summary?.smaPortfolioFee
        ?.selectedSubProducts,
    getHiddenIndexesMapFunc = () => undefined,
  }: SummaryPortfolioCompositionContainerProps) => {
    const { control } = useFormContext<AnalysisSummaryDTO>();

    const excludingSubTotals = useWatch({
      control,
      name: "hidePortfolioCompositionSubtotals",
    });

    const storedPortfolioCompositionData = useSelector(
      getPortfolioCompositionDataFunc,
    );
    const storedSmaSelectedSubProducts = useSelector(
      getSmaSelectedSubProductsFunc,
    );
    const storedHiddenIndexesMap = useSelector(getHiddenIndexesMapFunc);

    const storedRecommendationsData = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.summary?.productRecommendations,
    );

    const itemProductIdsMap = useMemo(() => {
      const map: Record<string, string> = {};
      storedSmaSelectedSubProducts?.forEach((item) => {
        const key = `${item.id}-${item.rateCardVersion ?? 0}`;
        map[key] = item.productId;
      });

      return map;
    }, [storedSmaSelectedSubProducts]);

    const selectedSetups = useMemo(() => {
      const mappedSetups = storedPortfolioCompositionData?.setups?.map(
        (setup) => ({
          ...setup,
          productId:
            itemProductIdsMap[`${setup.id}-${setup.rateCardVersion ?? 0}`],
        }),
      );

      const sortedSetups = sortBySubProductOrder({
        data: mappedSetups || [],
        getProductId: (i) => i.productId || "",
        productRecommendations: storedRecommendationsData,
      });

      return sortedSetups?.filter(
        (_, index) =>
          !storedHiddenIndexesMap || !storedHiddenIndexesMap?.[index],
      );
    }, [
      itemProductIdsMap,
      storedPortfolioCompositionData?.setups,
      storedRecommendationsData,
      storedHiddenIndexesMap,
    ]);

    const chunkSelectedSetups = useMemo(() => {
      const chunkSize = Configs.PortfolioCompositionNumOfItemsInTable;

      return chunk(selectedSetups, chunkSize);
    }, [selectedSetups]);

    const selectedSections = useMemo(() => {
      const mappedSections = storedPortfolioCompositionData?.sections?.map(
        (section) => {
          const filteredRows = section.rows?.filter((row) => {
            const { isSubTotal } = row;

            return !excludingSubTotals || !isSubTotal;
          });

          const mappedRows = filteredRows?.map((row) => {
            const mappedCells = row.cells?.map((cell) => ({
              ...cell,
              productId:
                itemProductIdsMap[
                  `${cell.setupId}-${cell.rateCardVersion ?? 0}`
                ],
            }));

            const sortedCells = sortBySubProductOrder({
              data: mappedCells || [],
              getProductId: (cell) => cell.productId || "",
              productRecommendations: storedRecommendationsData,
            });

            return {
              ...row,
              isSingleRow: filteredRows.length === 1,
              cells: sortedCells?.filter(
                (_, index) =>
                  !storedHiddenIndexesMap || !storedHiddenIndexesMap?.[index],
              ),
            };
          });

          return {
            ...section,
            rows: mappedRows,
          };
        },
      );

      return mappedSections;
    }, [
      excludingSubTotals,
      itemProductIdsMap,
      storedPortfolioCompositionData?.sections,
      storedRecommendationsData,
      storedHiddenIndexesMap,
    ]);

    const chunkSelectedSections = useMemo(() => {
      const numOfSetups = selectedSetups?.length || 0;

      if (numOfSetups === 0 || !selectedSections) return [];

      const setupsPerChunk = Configs.PortfolioCompositionNumOfItemsInTable;
      const numOfChunks = Math.ceil(numOfSetups / setupsPerChunk);

      const sectionsChunks: PortfolioCompositionSectionDTO[][] = [];

      for (let i = 0; i < numOfChunks; i++) {
        const startSetupIndex = i * setupsPerChunk;
        const endSetupIndex = startSetupIndex + setupsPerChunk;

        const chunkedSections = selectedSections.map((section) => {
          const chunkedRows = section.rows?.map((row) => {
            const chunkedCells = row.cells?.slice(
              startSetupIndex,
              endSetupIndex,
            );

            return {
              ...row,
              cells: chunkedCells,
            };
          });

          return {
            ...section,
            rows: chunkedRows,
          };
        });

        sectionsChunks.push(chunkedSections);
      }

      return sectionsChunks;
    }, [selectedSections, selectedSetups?.length]);

    return (
      <SHStack gap={2}>
        {chunkSelectedSetups.map((setupsChunk, index) => {
          const startSetupIndex =
            index * Configs.PortfolioCompositionNumOfItemsInTable + 1;
          const endSetupIndex = startSetupIndex + setupsChunk.length - 1;

          const tableIndicator =
            setupsChunk.length === 1
              ? `(Product ${startSetupIndex} of ${selectedSetups?.length})`
              : `(Products ${startSetupIndex}-${Math.min(
                  endSetupIndex,
                  selectedSetups?.length,
                )} of ${selectedSetups.length})`;

          return (
            <SummaryPortfolioCompositionTable
              key={index}
              disabled={disabled}
              showControls={showControls && index === 0}
              setups={setupsChunk}
              sections={chunkSelectedSections[index]}
              title={
                <SHStack direction={"row"} gap={1}>
                  {typeof title === "string" ? (
                    <SHTypography disabled={disabled} variant="subtitle2">
                      {title}
                    </SHTypography>
                  ) : (
                    title
                  )}
                  <SHTypography
                    disabled={disabled}
                    variant={"subtitle2"}
                    fontWeight={400}
                  >
                    {tableIndicator}
                  </SHTypography>
                </SHStack>
              }
            />
          );
        })}
      </SHStack>
    );
  },
);
